import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { RegionApiFrom } from '@api-gen/models/region-api-from';
import { CountryCodeApiRoaming } from '@api-roaming/models/country-code-api-roaming';
import { ForgotPasswordApiRoaming } from '@api-roaming/models/forgot-password-api-roaming';
import { ForgotPasswordEmailApiRoaming } from '@api-roaming/models/forgot-password-email-api-roaming';
import { GetConfcodeInfoResponseApiRoaming } from '@api-roaming/models/get-confcode-info-response-api-roaming';
import { ListCommunicChannelResponseApiRoaming } from '@api-roaming/models/list-communic-channel-response-api-roaming';
import { ErrorResponse } from '@api/models';
import { RecoverFormComponent } from '@auth/components/recover-form/recover-form.component';
import { SettingsAsObject } from '@core/models/settings.model';
import { StartWithUnit } from '@core/operators/start-with-unit';
import { RouteEntity } from '@core/router/router.entity';
import { AppState } from '@core/state/app.state';
import { UserState } from '@core/state/user.state';
import { TranslateModule } from '@ngx-translate/core';
import { RxEffects } from '@rx-angular/state/effects';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';


/**
 * Password recovery page
 */
@Component({
    selector: 'app-recover-page',
    templateUrl: './recover-page.component.html',
    styleUrls: ['./recover-page.component.scss'],
    standalone: true,
    imports: [
        RouterLink,
        AsyncPipe,
        TranslateModule,
        MatIconModule,
        RecoverFormComponent,

    ],
})
export class RecoverPageComponent {
    readonly #appState: AppState = inject(AppState);
    readonly #userState: UserState = inject(UserState);
    readonly #effects: RxEffects = inject(RxEffects);

    public readonly pending$: Observable<boolean> = this.#userState.loading$.pipe(
        map((loading: Record<StartWithUnit, boolean>) => loading?.[StartWithUnit.RecoverPassword]),
    );

    public readonly error$: Observable<ErrorResponse> = this.#userState.errors$.pipe(
        map((errors: Record<StartWithUnit, ErrorResponse>) => errors?.[StartWithUnit.RecoverPassword]),
    );

    public readonly loggedIn$: Observable<boolean> = this.#userState.isAuthorized$;
    public readonly countryCodes$: Observable<CountryCodeApiRoaming[]> = this.#appState.countryCodes$;
    public readonly defaultCode$: Observable<CountryCodeApiRoaming> = this.#appState.defaultCountryCode$;
    public readonly regions$: Observable<RegionApiFrom[]> = this.#appState.regions$;
    public readonly settings$: Observable<SettingsAsObject> = this.#appState.settings$;

    public readonly communicationChannels$: Observable<ListCommunicChannelResponseApiRoaming[]> = this.#appState.communicationChannels$;
    public readonly communicationChannelsText$: Observable<GetConfcodeInfoResponseApiRoaming> = this.#appState.communicationChannelsInfo$;

    public readonly routeEntity: typeof RouteEntity = RouteEntity;

    constructor() {
        this.#effects.register(
            this.loggedIn$.pipe(filter(Boolean)),
            () => this.#userState.logout(),
        );
    }

    public onSubmit(data: ForgotPasswordApiRoaming & ForgotPasswordEmailApiRoaming): void {
        if (data?.phoneNumber) {
            this.#userState.recoverByPhone(data);
        } else {
            this.#userState.recoverByEmail(data);
        }
    }
}
